import type {
  UserGender,
  UserSalutation,
} from '../../../shared/UserInterface';

export interface SelectOption<T> {
  value: T;
  label: string;
}

export const SALUTATION_OPTIONS: SelectOption<UserSalutation>[] = [
  { value: 'herr', label: 'Herr' },
  { value: 'frau', label: 'Frau' },
  { value: 'divers', label: 'Divers' },
  { value: 'keine_angabe', label: 'Keine Angabe' },
];

export const GENDER_OPTIONS: SelectOption<UserGender>[] = [
  { value: 'male', label: 'Männlich' },
  { value: 'female', label: 'Weiblich' },
  { value: 'diverse', label: 'Divers' },
  { value: 'prefer_not_to_say', label: 'Keine Angabe' },
];

/**
 * Sentinel value for "the user is from a country not in our short list".
 * Picking this in the dropdown reveals a free-text ISO-code input where
 * the user types the 2-letter code of their country (e.g. "FR", "US").
 * It is never persisted — the resolved 2-letter code is saved instead.
 */
export const COUNTRY_OTHER = 'OTHER';

export const COUNTRY_OPTIONS: SelectOption<string>[] = [
  { value: 'DE', label: 'Deutschland' },
  { value: 'AT', label: 'Österreich' },
  { value: 'CH', label: 'Schweiz' },
  { value: COUNTRY_OTHER, label: 'Anderes Land' },
];

/** Country codes whose label is displayed as a dropdown entry. */
const LISTED_COUNTRY_CODES = new Set(['DE', 'AT', 'CH']);

/** True when a country code has its own dropdown entry. */
export function isListedCountry(code: string | null | undefined): boolean {
  return !!code && LISTED_COUNTRY_CODES.has(code);
}

/** Permissive phone pattern, mirrors the backend regex. */
export const PHONE_PATTERN = /^[\d+()\-\s]{5,30}$/;

/**
 * Country-specific postal-code constraints.
 * - DE: exactly 5 digits
 * - AT, CH: exactly 4 digits
 * - default (no country selected): digits-only, any length
 *
 * Sources: https://en.wikipedia.org/wiki/List_of_postal_codes
 */
export interface PostalCodeFormat {
  pattern: RegExp;
  maxLength: number;
  /** Human-readable German requirement, used for the inline error message. */
  requirement: string;
}

export const DEFAULT_POSTAL_CODE_FORMAT: PostalCodeFormat = {
  pattern: /^\d*$/,
  maxLength: 20,
  requirement: 'PLZ darf nur Ziffern enthalten',
};

const POSTAL_CODE_FORMATS: Record<string, PostalCodeFormat> = {
  DE: {
    pattern: /^\d{5}$/,
    maxLength: 5,
    requirement: 'PLZ muss aus genau 5 Ziffern bestehen',
  },
  AT: {
    pattern: /^\d{4}$/,
    maxLength: 4,
    requirement: 'PLZ muss aus genau 4 Ziffern bestehen',
  },
  CH: {
    pattern: /^\d{4}$/,
    maxLength: 4,
    requirement: 'PLZ muss aus genau 4 Ziffern bestehen',
  },
};

export function getPostalCodeFormat(
  country: string | null | undefined,
): PostalCodeFormat {
  if (!country) return DEFAULT_POSTAL_CODE_FORMAT;
  return POSTAL_CODE_FORMATS[country] ?? DEFAULT_POSTAL_CODE_FORMAT;
}
